
import React from "react";
import { Button } from "@/components/ui/button";
import { Eye, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import { JournalEntry } from "@/hooks/journal/types";
import ExportButton from "@/components/export/ExportButton";
import { useIsMobile } from "@/hooks/use-mobile";

interface JournalEntryCardActionsProps {
  entryId: string;
  entry?: JournalEntry;
  onView: (entryId: string) => void;
  onDelete: (entryId: string) => void;
  isDeleting: boolean;
  entryToDelete: string | null;
}

const JournalEntryCardActions: React.FC<JournalEntryCardActionsProps> = ({
  entryId,
  entry,
  onView,
  onDelete,
  isDeleting,
  entryToDelete
}) => {
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);
  const isMobile = useIsMobile();

  return (
    <div className="flex gap-1 sm:gap-2">
      <Button 
        variant="outline" 
        size={isMobile ? "sm" : "sm"}
        onClick={() => onView(entryId)}
        className={cn(
          "flex items-center min-h-[44px] min-w-[44px]",
          isMobile ? "px-2 gap-1" : "gap-1",
          enableRpgMode ? (
            rpgTheme.button + " text-amber-100"
          ) : (
            darkMode 
              ? "bg-[#6127A0]/20 hover:bg-[#6127A0]/30 text-[#FBF3F1]" 
              : "bg-[#6127A0]/10 hover:bg-[#6127A0]/20 text-[#1E0C46]"
          )
        )}
      >
        <Eye className="h-4 w-4" />
        {!isMobile && <span>{enableRpgMode ? "Review" : "View"}</span>}
      </Button>

      {entry && (
        <ExportButton
          entries={[entry]}
          variant="outline"
          size={isMobile ? "sm" : "sm"}
          showText={!isMobile}
          className={cn(
            "min-h-[44px] min-w-[44px]",
            isMobile && "px-2",
            enableRpgMode ? (
              rpgTheme.button + " text-amber-100"
            ) : (
              darkMode 
                ? "bg-[#6127A0]/20 hover:bg-[#6127A0]/30 text-[#FBF3F1]" 
                : "bg-[#6127A0]/10 hover:bg-[#6127A0]/20 text-[#1E0C46]"
            )
          )}
        />
      )}
      
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button 
            variant="outline" 
            size={isMobile ? "sm" : "sm"}
            className={cn(
              "flex items-center text-destructive min-h-[44px] min-w-[44px]",
              isMobile ? "px-2 gap-1" : "gap-1",
              enableRpgMode ? (
                "bg-red-900/50 hover:bg-red-900/70 border-red-600/50 text-red-300"
              ) : (
                darkMode 
                  ? "bg-destructive/20 hover:bg-destructive/30 border-destructive/30" 
                  : "bg-destructive/10 hover:bg-destructive/20 border-destructive/30"
              )
            )}
          >
            <Trash2 className="h-4 w-4" />
            {!isMobile && <span>{enableRpgMode ? "Discard" : "Delete"}</span>}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="mx-4 max-w-sm sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {enableRpgMode 
                ? "This will permanently remove this quest from your chronicle. This action cannot be undone."
                : "This will permanently delete this journal entry. This action cannot be undone."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => onDelete(entryId)} 
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto"
            >
              {isDeleting && entryToDelete === entryId ? "Discarding..." : enableRpgMode ? "Discard" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JournalEntryCardActions;
